import React, { useEffect, useRef, useState } from 'react';
import './_configurationBot.scss';
import { injectIntl } from 'react-intl';
import {
  IDENTITY_ICON_TYPES,
  MESSAGE_TYPE_APPROVE,
  MESSAGE_TYPE_COPY,
  MESSAGE_TYPE_INFERENCE,
  MESSAGE_TYPE_TEXT,
} from './constants';
import CapRow from '../../CapRow';
import IdentityIcon from './IdentityIcon';
import UserApproveAction from './UserApproveAction';
import { InferenceNode } from './inferenceGenerator';
import CapButton from '../../CapButton';
import CapExpressionEditor from '../../CapExpressionEditor';
import { clsPrefix } from '.';
import { getGrammar } from './util';
import { rootClsPrefix } from '../constants';

const _ = require('lodash');

const editBorder = '2px solid black';

export const Chevron = ({
  direction = 'right',
  size = '0.45rem',
  style = {},
  ...rest
}) => {
  let rotation = '0deg';
  if (direction === 'down') {
    rotation = '90deg';
  } else if (direction === 'left') {
    rotation = '180deg';
  } else if (direction === 'up') {
    rotation = '270deg';
  }

  return (
    <svg
      viewBox="0 0 100 100"
      style={{
        width: size,
        height: size,
        transform: `rotate(${rotation})`,
        cursor: 'pointer',
        ...style,
      }}
      {...rest}
    >
      <polygon points="0,0 100,50 0,100" fill="black" />
    </svg>
  );
};

const EditableTextNode = ({ node, setChanges, disabled }) => {
  const value = node.value;
  const [text, setText] = useState(value.content);

  const handleChange = e => {
    let val = e.target.value;
    if (value.inputType === 'number') {
      val = parseFloat(val);
    }
    setText(val);
    setChanges(changes => {
      const pathKey = node.path.join('.');
      const newChanges = { ...changes };
      newChanges[pathKey] = {
        path: node.path,
        original: value.content,
        value: val,
      };
      return newChanges;
    });
  };
  return (
    <input
      type={value.inputType}
      className={`${rootClsPrefix}-raw-input`}
      value={text}
      onChange={handleChange}
      style={{
        flexGrow: 1,
        borderRight: disabled ? 'none' : editBorder,
      }}
      disabled={disabled}
    />
  );
};

const ExpressionNode = ({
  node,
  request,
  getAPICallObject,
  setLocalBlockers,
  setChanges,
  disabled,
}) => {
  const value = node.value;
  const errorId = useRef(_.uniqueId());
  const [grammar, setGrammar] = useState();
  const [expression, setExpression] = useState(value.expression);

  const handleChange = (expressionJSON, expression, isError) => {
    setExpression(expression);
    setChanges(changes => {
      const pathKey = node.path.join('.');
      const expressionPathKey = `${pathKey}.expression`;
      const expressionJSONPathKey = `${pathKey}.expressionJSON`;
      const newChanges = { ...changes };
      if (expression !== value.expression) {
        newChanges[expressionPathKey] = {
          path: [...node.path, 'expression'],
          original: value.expression,
          value: expression,
        };
      } else {
        delete newChanges[expressionPathKey];
      }
      newChanges[expressionJSONPathKey] = {
        path: [...node.path, 'expressionJSON'],
        original: '',
        value: expressionJSON,
      };
      return newChanges;
    });
    if (isError) {
      setLocalBlockers(localBlockers => {
        const newBlockers = {
          ...localBlockers,
          [errorId.current]: 'Invalid rule expressions present.',
        };
        return newBlockers;
      });
    } else {
      setLocalBlockers(localBlockers => {
        const newBlockers = { ...localBlockers };
        delete newBlockers[errorId.current];
        return newBlockers;
      });
    }
  };

  useEffect(() => {
    if (disabled) return;
    const blockerId = _.uniqueId();
    setLocalBlockers(localBlockers => {
      const newBlockers = {
        ...localBlockers,
        [blockerId]: 'Loading grammar...',
      };
      return newBlockers;
    });
    getGrammar({
      request,
      getAPICallObject,
      activity: value.activity,
    }).then(grammar => {
      setGrammar(grammar);
      setLocalBlockers(localBlockers => {
        const newBlockers = { ...localBlockers };
        delete newBlockers[blockerId];
        return newBlockers;
      });
    });
  }, []);

  const enabled = !disabled && grammar;

  return (
    <div style={{ flexGrow: 1, position: 'relative' }}>
      {!enabled && (
        <div
          style={{
            fontFamily: 'monospace',
            borderRight: disabled ? 'none' : editBorder,
          }}
        >
          {expression}
        </div>
      )}
      {enabled && (
        <CapExpressionEditor
          data-testid="expression-editor"
          className={`${clsPrefix}-expression-editor`}
          expression={expression}
          onChange={handleChange}
          grammar={grammar}
        />
      )}
    </div>
  );
};

const NodeValue = ({
  node,
  request,
  getAPICallObject,
  setLocalBlockers,
  setChanges,
  disabled,
}) => {
  let value = node.value;

  if (typeof value === 'object') {
    if (value.type === 'RULE_EXPRESSION') {
      return (
        <ExpressionNode
          node={node}
          request={request}
          getAPICallObject={getAPICallObject}
          setLocalBlockers={setLocalBlockers}
          setChanges={setChanges}
          disabled={disabled}
        />
      );
    }
    if (value.type === 'EDITABLE_TEXT') {
      return (
        <EditableTextNode
          node={node}
          setChanges={setChanges}
          disabled={disabled}
        />
      );
    }
  }

  value = node.escapeValue ? _.escape(value) : value;
  return (
    <span
      style={{ fontSize: '0.95rem', ...node.css }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

/**
 * @param {{node: InferenceNode}} props
 */
const InferenceNodeView = ({
  node,
  request,
  getAPICallObject,
  setLocalBlockers = () => {},
  setChanges = () => {},
  disabled = false,
}) => {
  if (!node || !node.isValid()) {
    return <></>;
  }

  const [isOpen, setIsOpen] = useState(node.defaultOpen);

  const hasValue = node.value !== null;
  const hasChildren = node.children.length > 0;

  let name = node.displayName;
  if (hasValue) {
    name += ':';
  }

  const chevronSize = '0.85rem';

  return (
    <div className="inference-node" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'top' }}>
        {hasChildren ? (
          <>
            <div
              style={{
                position: 'absolute',
                width: '1px',
                height: 'calc(100% - 1rem)',
                backgroundColor: '#aaa',
                left: `calc(${chevronSize} / 2)`,
                top: chevronSize,
                display: isOpen ? 'block' : 'none',
              }}
            />
            <Chevron
              direction={isOpen ? 'down' : 'right'}
              size={chevronSize}
              onClick={e => {
                setIsOpen(!isOpen);
              }}
              style={{ padding: '0.15rem', alignSelf: 'center' }}
            />
          </>
        ) : (
          <div
            style={{
              width: chevronSize,
              height: chevronSize,
              flexShrink: 0,
            }}
          />
        )}
        <strong
          style={{
            marginLeft: '0.25rem',
            marginRight: '0.5rem',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </strong>
        {hasValue && (
          <NodeValue
            node={node}
            request={request}
            getAPICallObject={getAPICallObject}
            setLocalBlockers={setLocalBlockers}
            setChanges={setChanges}
            disabled={disabled}
          />
        )}
      </div>
      <div style={{ paddingLeft: '1rem', display: isOpen ? 'block' : 'none' }}>
        {node.children.map(
          child =>
            child ? (
              <InferenceNodeView
                key={child.displayName}
                node={child}
                request={request}
                getAPICallObject={getAPICallObject}
                setLocalBlockers={setLocalBlockers}
                setChanges={setChanges}
                disabled={disabled}
              />
            ) : (
              <></>
            ),
        )}
      </div>
    </div>
  );
};

const Inference = ({
  changes,
  setChanges,
  request,
  getAPICallObject,
  loading,
  message,
  isLastInference,
  handleSubmit,
  sendMessage,
  textCreate,
  textCancel,
}) => {
  const [localBlockers, setLocalBlockers] = useState({});
  const inference = InferenceNode.from_json(message.inference);
  const tips = message.tips;
  const blockers = message.blockers;

  const handleApproval = () => {
    sendMessage({
      type: MESSAGE_TYPE_APPROVE,
    });
  };

  const createBulletBlock = (title, items) => {
    return (
      <div
        style={{
          padding: '0.4rem 0.75rem',
          borderRadius: '0.5rem',
          backgroundColor: '#eaf1ff',
          margin: '1rem 0',
          boxShadow: '1px 1px 4px #aaa',
        }}
      >
        <div
          style={{ fontWeight: '500', marginTop: '0.5rem', fontSize: '1.1rem' }}
        >
          {title}
        </div>
        <ul>
          {items.map((item, index) => {
            return (
              <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      <div style={{ marginBottom: '0.5rem' }}>
        {`${
          message.actionName
        } is the action identified for your requirement. Please verify the below details.`}
      </div>
      <InferenceNodeView
        node={inference}
        request={request}
        getAPICallObject={getAPICallObject}
        setLocalBlockers={setLocalBlockers}
        setChanges={setChanges}
        disabled={!isLastInference}
      />
      <div style={{ marginTop: '0.5rem' }} />
      {tips.length > 0 && createBulletBlock('Callouts', tips)}
      {blockers.length > 0 &&
        createBulletBlock(
          'Following issues need to be resolved to proceed with the creation:',
          blockers,
        )}
      {isLastInference &&
        blockers.length === 0 && (
          <UserApproveAction
            loading={loading}
            handleSubmit={handleSubmit}
            handleApproval={handleApproval}
            textCreate={textCreate}
            textCancel={textCancel}
            localBlockers={localBlockers}
          />
        )}
    </>
  );
};

const CopyMessage = ({ message, handleSubmit, request, getAPICallObject }) => {
  const inference = InferenceNode.from_json(message.inferenceView);
  inference.defaultOpen = false;
  const encodedInfo = message.encodedInfo;
  navigator.clipboard?.writeText?.(encodedInfo);
  const encodedNode = new InferenceNode('Encoded', null, {
    defaultOpen: false,
    children: [new InferenceNode('Copy', encodedInfo)],
  });
  return (
    <div>
      <div>
        Copied action {message.chainName} to your clipboard. Paste it in the
        same window wherever you want to duplicate it.
      </div>
      <InferenceNodeView
        node={inference}
        request={request}
        getAPICallObject={getAPICallObject}
        disabled={true}
      />
      <InferenceNodeView node={encodedNode} />
      <div>
        Click to duplicate in this org:{' '}
        <CapButton
          style={{
            height: 'fit-content',
            width: 'fit-content',
            padding: '0.2rem',
            marginLeft: '0.3rem',
          }}
          onClick={e => {
            handleSubmit(encodedInfo);
          }}
        >
          Duplicate
        </CapButton>
      </div>
    </div>
  );
};

const CopilotResponse = ({ message, ...rest }) => {
  const msgType = message.type;
  let messageBlock = <></>;
  if (msgType === MESSAGE_TYPE_TEXT) {
    messageBlock = <div data-testid="response-desc">{message.message}</div>;
  } else if (msgType === MESSAGE_TYPE_INFERENCE) {
    messageBlock = <Inference message={message} {...rest} />;
  } else if (msgType === MESSAGE_TYPE_COPY) {
    messageBlock = <CopyMessage message={message} {...rest} />;
  }
  return (
    <CapRow
      type="flex"
      className="generate-response-container"
      data-testid="copilot-response"
    >
      <IdentityIcon iconType={IDENTITY_ICON_TYPES.COPILOT} />
      <div
        className="generate-response-description"
        data-testid="response-desc"
      >
        {messageBlock}
      </div>
    </CapRow>
  );
};

CopilotResponse.defaultProps = {};
export default injectIntl(CopilotResponse);
